import { useIsMutating, useMutation, useQuery } from '@tanstack/react-query';

import { ApiClientError } from '@/lib/api';
import {
    createCheckoutSession,
    createCustomerPortalSession,
    getOrganizationBilling,
    listBillingProducts,
    type CreateStripeCheckoutSessionInput,
    type CreateStripeCustomerPortalSessionInput
} from '../services/billing';
import { billingKeys } from '../services/queryKeys';

type CreateCheckoutSessionOptions = {
    allowMockFallback?: boolean;
};

export function useBillingProducts() {
    return useQuery({
        queryKey: billingKeys.products(),
        queryFn: listBillingProducts
    });
}

export function useOrganizationBilling(organizationId: string | null) {
    return useQuery({
        queryKey: organizationId ? billingKeys.organization(organizationId) : billingKeys.all,
        queryFn: () => getOrganizationBilling(organizationId!),
        enabled: Boolean(organizationId)
    });
}

export function useCreateCheckoutSession(organizationId: string | null, options: CreateCheckoutSessionOptions = {}) {
    return useMutation({
        mutationKey: billingKeys.checkoutSessionMutation(organizationId),
        mutationFn: async ({ productId, input }: { productId: string; input: CreateStripeCheckoutSessionInput }) => {
            if (!organizationId) {
                throw new ApiClientError({ message: 'No organization selected', status: 400 });
            }

            return createCheckoutSession({
                organizationId,
                productId,
                input,
                allowMockFallback: options.allowMockFallback
            });
        }
    });
}

/**
 * Cache-derived pending state for checkout-session creation. Subscribes to
 * the mutation cache instead of a hook-level observer so the value cannot go
 * stale when an observer detaches from an in-flight mutation (StrictMode
 * effect remounts).
 */
export function useIsCreatingCheckoutSession(organizationId: string | null): boolean {
    return useIsMutating({ mutationKey: billingKeys.checkoutSessionMutation(organizationId) }) > 0;
}

export function useCreateCustomerPortalSession(organizationId: string | null) {
    return useMutation({
        mutationFn: async (input: CreateStripeCustomerPortalSessionInput) => {
            if (!organizationId) {
                throw new ApiClientError({ message: 'No organization selected', status: 400 });
            }

            return createCustomerPortalSession({
                organizationId,
                input
            });
        }
    });
}
